import { saveAs } from "file-saver";
import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { myAxios } from "src/components/common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";

import { extraFields } from "./ProposalList";
import { fields } from "./ProposalFilterForm";

export async function proposalsLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if (![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }
    const formData = new FormData();
    formData.append("page", url.searchParams.get("page")!);
    formData.append("limit", url.searchParams.get("limit")!);
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({ fileName: "proposals/readProposals.php", formData });
    if (!res) throw redirect("..");
    return res.data;
}

export async function proposalLoader({ params }: LoaderFunctionArgs) {
    const { id } = params;
    if (!id) {
        throw redirect("/Proposals");
    }
    const formData = new FormData();
    formData.append("id", id);
    const res = await myAxios({ fileName: "proposals/readProposal.php", formData });
    if (!res || !res.data) throw redirect("..");
    return {
        proposal: res.data,
    };
}

const urlMap = {
    Add: "proposals/createProposal.php",
    Edit: "proposals/updateProposal.php",
};

export async function proposalAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    const action = formData.get("action") as keyof typeof urlMap;
    const url = urlMap[action];
    if (!url) {
        window.alert("Invalid proposal action.");
        return null;
    }
    if (!formData.get("proposalNumber")) {
        window.alert("Proposal number is required.");
        return null;
    }
    if (!formData.get("requesterId")) {
        window.alert("Please select requester.");
        return null;
    }
    if (!formData.get("approverId")) {
        window.alert("Please select approver.");
        return null;
    }
    const duplicateCheck = new FormData();
    duplicateCheck.append("proposalNumber", String(formData.get("proposalNumber")));
    if (formData.get("id")) {
        duplicateCheck.append("id", String(formData.get("id")));
    }
    const duplicateRes = await myAxios({
        fileName: "proposals/checkDuplicateProposal.php",
        formData: duplicateCheck,
        signal: controller.signal,
    });
    if (!duplicateRes) return null;
    if (duplicateRes.data?.id) {
        window.alert(`Proposal number duplicated with ${duplicateRes.data.proposalNumber}.`);
        return null;
    }
    const lines = JSON.parse(String(formData.get("data") ?? "[]")) as { description?: string }[];
    if (!Array.isArray(lines) || !lines.length || !lines.some(line => (line.description ?? "").trim())) {
        window.alert("At least one line item is required.");
        return null;
    }
    const res = await myAxios({ fileName: url, formData, signal: controller.signal });
    if (!res) return null;
    if (action === "Add") {
        return redirect(`/Proposals/${res.data.id}`);
    }
    return redirect("..");
}

async function runSimpleAction(request: Request, fileName: string, fallback = "..") {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName,
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect(fallback);
}

export async function deleteProposalAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "proposals/deleteProposal.php", "../..");
    throw redirect("../..");
}

export async function voidProposalAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "proposals/voidProposal.php");
    throw redirect("..");
}

export async function validateProposalAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "proposals/validateProposal.php");
    throw redirect("..");
}

export async function notifyProposalAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "proposals/notifyProposal.php");
    throw redirect("..");
}

export async function approveProposalAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "proposals/approveProposal.php");
    throw redirect("..");
}

export async function proposalExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: "proposals/exportProposals.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);
    const disposition = res.headers["content-disposition"];
    let filename = "Proposals.csv";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(new Blob([res.data]), filename);
    throw redirect(`..?${url.searchParams.toString()}`);
}
